import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2, Check, CreditCard, Star, Zap, Phone, Globe, MapPin } from 'lucide-react';
import { useBusinessAuth } from '@/context/BusinessAuthContext';
import { toast } from 'sonner';

const API_URL = '';

const CORE_FEATURES = [
  { text: 'Phone contact button', icon: Phone },
  { text: 'Website link', icon: Globe },
  { text: 'Map directions', icon: MapPin },
  { text: 'Full portal access', icon: Check },
  { text: 'Standard placement', icon: Check },
];

const BOOST_FEATURES = [
  { text: 'Featured badge', icon: Star },
  { text: 'Priority placement', icon: Zap },
  { text: 'Enhanced map visibility', icon: MapPin },
];

export default function PortalBillingPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshUser } = useBusinessAuth();
  
  const [loading, setLoading] = useState(true);
  const [subData, setSubData] = useState(null);
  const [togglingBoost, setTogglingBoost] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/partner/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchSubscription();
    }
  }, [user]);

  const fetchSubscription = async () => {
    try {
      const response = await fetch(`${API_URL}/api/partner/portal/subscription`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubData(data);
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFeaturedBoost = async (enable) => {
    setTogglingBoost(true);
    try {
      const response = await fetch(`${API_URL}/api/partner/portal/featured-boost`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enable })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update Featured Boost');
      }

      const result = await response.json();
      
      if (result.action === 'checkout' && result.url) {
        // Redirect to Stripe checkout for adding Featured Boost
        window.location.href = result.url;
      } else {
        // Boost was disabled
        toast.success('Featured Boost has been removed');
        await fetchSubscription();
        await refreshUser();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update Featured Boost');
    } finally {
      setTogglingBoost(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  const hasFeaturedBoost = subData?.pricing?.has_featured_boost || false;
  const corePrice = subData?.pricing?.core_listing?.price || 199;
  const boostPrice = subData?.pricing?.featured_boost?.price || 99;
  const totalPrice = subData?.pricing?.total_price || corePrice;
  const billingStatus = subData?.subscription?.billing_status || 'current';
  const subscriptionStatus = subData?.subscription?.status || 'inactive';

  // Status badge styling
  const getStatusBadge = () => {
    if (subscriptionStatus === 'active' && billingStatus === 'current') {
      return { bg: 'bg-green-100', text: 'text-green-700', label: 'Active' };
    } else if (billingStatus === 'overdue') {
      return { bg: 'bg-red-100', text: 'text-red-700', label: 'Payment Overdue' };
    } else if (subscriptionStatus === 'canceled') {
      return { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Canceled' };
    } else if (subscriptionStatus === 'past_due') {
      return { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Past Due' };
    }
    return { bg: 'bg-slate-100', text: 'text-slate-600', label: subscriptionStatus };
  };

  const statusBadge = getStatusBadge();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portal-billing-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <Link 
            to="/portal"
            className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-700" />
          </Link>
          <h1 className="text-xl font-bold text-slate-900 font-heading">
            Billing
          </h1>
        </div>
      </header>

      {/* Content */}
      <main className="px-6 py-8">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Billing Alert for Overdue */}
          {billingStatus === 'overdue' && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-4 h-4 text-red-600" />
              </div>
              <div>
                <p className="font-semibold text-red-900">Payment Failed</p>
                <p className="text-sm text-red-700">Please update your payment method to keep your listing active.</p>
              </div>
            </div>
          )}

          {/* Current Plan Summary */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900 font-heading">Current Plan</h2>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                {statusBadge.label}
              </span>
            </div>
            
            <div className="flex items-baseline gap-2 mb-4">
              <span className="text-3xl font-bold text-slate-900">${totalPrice}</span>
              <span className="text-slate-500">/month</span>
            </div>

            <div className="flex items-center gap-2 flex-wrap mb-4">
              <span className="px-4 py-2 rounded-full bg-teal-100 text-teal-700 font-medium text-sm">
                Core Listing
              </span>
              {hasFeaturedBoost && (
                <span className="px-4 py-2 rounded-full bg-amber-100 text-amber-700 font-medium text-sm flex items-center gap-1">
                  <Star className="w-3 h-3" />
                  Featured Boost
                </span>
              )}
            </div>
          </div>

          {/* Core Listing Details */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-slate-900 font-heading">Core Listing</h3>
                <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs font-medium rounded-full">INCLUDED</span>
              </div>
              <span className="text-lg font-bold text-slate-900">${corePrice}/mo</span>
            </div>

            <ul className="space-y-2">
              {CORE_FEATURES.map((feature) => (
                <li key={feature.text} className="flex items-center gap-2 text-sm text-slate-600">
                  <div className="w-5 h-5 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center">
                    <feature.icon className="w-3 h-3" />
                  </div>
                  {feature.text}
                </li>
              ))}
            </ul>
          </div>

          {/* Featured Boost Section */}
          <div className={`rounded-2xl p-6 border-2 shadow-sm transition-all ${
            hasFeaturedBoost 
              ? 'bg-gradient-to-br from-amber-50 to-white border-amber-200' 
              : 'bg-white border-slate-100'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-slate-900 font-heading">Featured Boost</h3>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                  hasFeaturedBoost 
                    ? 'bg-amber-100 text-amber-700' 
                    : 'bg-slate-100 text-slate-600'
                }`}>
                  {hasFeaturedBoost ? 'ACTIVE' : 'ADD-ON'}
                </span>
              </div>
              <span className="text-lg font-bold text-slate-900">+${boostPrice}/mo</span>
            </div>

            <ul className="space-y-2 mb-6">
              {BOOST_FEATURES.map((feature) => (
                <li key={feature.text} className="flex items-center gap-2 text-sm text-slate-600">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                    hasFeaturedBoost 
                      ? 'bg-amber-100 text-amber-600' 
                      : 'bg-slate-100 text-slate-400'
                  }`}>
                    <feature.icon className="w-3 h-3" />
                  </div>
                  {feature.text}
                </li>
              ))}
            </ul>

            {hasFeaturedBoost ? (
              <Button 
                variant="outline"
                onClick={() => handleToggleFeaturedBoost(false)}
                disabled={togglingBoost}
                className="w-full h-11 rounded-full border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="remove-boost-btn"
              >
                {togglingBoost ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Remove Featured Boost'
                )}
              </Button>
            ) : (
              <Button 
                onClick={() => handleToggleFeaturedBoost(true)}
                disabled={togglingBoost}
                className="w-full h-11 rounded-full bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="add-boost-btn"
              >
                {togglingBoost ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Star className="w-4 h-4 mr-2" />
                    Add Featured Boost
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Payment Method */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900 font-heading mb-4">
              Payment Method
            </h2>
            
            <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
              <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-slate-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900">Card ending in ****</p>
                <p className="text-xs text-slate-500">Managed by Stripe</p>
              </div>
            </div>

            <p className="text-xs text-slate-500 mt-4">
              For billing questions, contact us at hello@disqover.com.au
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
